import { useCallback, useMemo, useRef, useState, type RefObject } from "react";
import {
  focusTopMostError,
  validateAll,
  validateField,
  type Focusable,
  type FocusableRef,
  type ValidationConfig,
} from "./validation";

export interface ValidatedForm<F extends string> {
  values: Record<F, string>;
  errors: Partial<Record<F, string>>;
  /**
   * Gán vào prop `ref` của Input — thứ tự trong `fieldOrder` quyết định "lỗi
   * trên cùng". Generic vì mỗi component Cloudscape có kiểu ref riêng, nhưng
   * hook chỉ cần tới `focus()`.
   */
  refFor: <T extends Focusable>(field: F) => RefObject<T | null>;
  setValue: (field: F, value: string) => void;
  handleBlur: (field: F) => void;
  /** Trả về true khi hợp lệ. Nếu không, đã tự focus vào field lỗi đầu tiên. */
  submit: () => boolean;
  reset: () => void;
  setServerError: (field: F, message: string) => void;
}

/**
 * Hành vi validation theo đúng quy tắc Cloudscape:
 * - Không validate ở lần vào trang đầu tiên.
 * - Validate khi blur (field đã nhập, hoặc field bắt buộc bị bỏ trống).
 * - Khi field đang đỏ thì validate lại theo từng phím gõ, để lỗi biến mất ngay
 *   lúc người dùng sửa xong thay vì phải chờ submit lại.
 * - Submit thất bại thì focus vào field lỗi trên cùng.
 */
export function useValidatedForm<F extends string>(
  config: ValidationConfig,
  initial: Record<F, string>,
  /** Thứ tự hiển thị trên trang. Quyết định field nào là "trên cùng". */
  fieldOrder: readonly F[],
): ValidatedForm<F> {
  const [values, setValues] = useState<Record<F, string>>(initial);
  const [errors, setErrors] = useState<Partial<Record<F, string>>>({});
  // Bản sao đồng bộ của values: submit cần đọc giá trị mới nhất mà không phải
  // luồn qua state updater.
  const valuesRef = useRef<Record<F, string>>(initial);
  const refs = useRef<Record<string, FocusableRef>>({});

  const refFor = useCallback(<T extends Focusable>(field: F): RefObject<T | null> => {
    refs.current[field] ??= { current: null };
    return refs.current[field] as RefObject<T | null>;
  }, []);

  const orderedRefs = useMemo(() => {
    const map: Record<string, FocusableRef> = {};
    for (const field of fieldOrder) map[field] = refFor(field);
    return map;
  }, [fieldOrder, refFor]);

  const setValue = useCallback(
    (field: F, value: string) => {
      valuesRef.current = { ...valuesRef.current, [field]: value };
      setValues(valuesRef.current);
      setErrors((previous) => (previous[field] ? { ...previous, [field]: validateField(config, field, value) } : previous));
    },
    [config],
  );

  const handleBlur = useCallback(
    (field: F) => {
      const value = valuesRef.current[field] ?? "";
      const hasRules = (config[field] || []).length > 0;
      if (!value.trim() && !hasRules) return;
      setErrors((previous) => ({ ...previous, [field]: validateField(config, field, value) }));
    },
    [config],
  );

  const submit = useCallback(() => {
    const next = validateAll(config, valuesRef.current) as Partial<Record<F, string>>;
    setErrors(next);
    if (Object.keys(next).length > 0) {
      focusTopMostError(orderedRefs, next);
      return false;
    }
    return true;
  }, [config, orderedRefs]);

  const initialRef = useRef(initial);
  const reset = useCallback(() => {
    valuesRef.current = initialRef.current;
    setValues(initialRef.current);
    setErrors({});
  }, []);

  const setServerError = useCallback((field: F, message: string) => {
    setErrors((previous) => ({ ...previous, [field]: message }));
  }, []);

  return { values, errors, refFor, setValue, handleBlur, submit, reset, setServerError };
}
